// hooks/useSystemStatus.js
import { useState, useEffect, useCallback, useMemo } from 'react';

// API endpoints - consider moving to constants file
const API_ENDPOINTS = {
    SYSTEM_STATUS: '/analytics/v2/system/status',
    REFRESH_PROGRESS: '/analytics/v2/system/refresh-progress',
    REFRESH_PROGRESS_STREAM: '/analytics/v2/system/refresh-progress/stream',
    REFRESH_START: '/analytics/v2/system/refresh',
    REFRESH_STOP: '/analytics/v2/system/refresh/stop',
    DATASETS_GENERATE: '/analytics/v2/system/datasets',
    PIPELINE_START: '/analytics/v2/system/pipeline/start',
    PIPELINE_STATUS: '/analytics/v2/system/pipeline/status',
    PIPELINE_STOP: '/analytics/v2/system/pipeline/stop'
};

// Polling configuration
const POLLING_CONFIG = {
    REFRESH_PROGRESS_INTERVAL: 2000, // 2 seconds
    SSE_TIMEOUT: 15000 // 15 seconds for keep-alive
};

const RETRY_CONFIG = {
    INITIAL_RETRIES: 1,
    RETRY_DELAY_MS: 2000
};

export const useSystemStatus = () => {
    const [systemStatus, setSystemStatus] = useState({
        dataFreshness: null,
        refreshHistory: [],
        cronStatus: null,
        databaseHealth: null,
        loading: true,
        refreshing: false,
        error: null
    });

    const [refreshProgress, setRefreshProgress] = useState({
        isRunning: false,
        progress: 0,
        currentPhase: null,
        details: null,
        logs: [],
        refreshId: null,
        startTime: null,
        source: null
    });

    const [refreshStream, setRefreshStream] = useState(null);

    const fetchSystemStatus = useCallback(async (withRetry = false, retryCount = RETRY_CONFIG.INITIAL_RETRIES) => {
        try {
            setSystemStatus(prev => {
                const hasExistingData = Boolean(
                    prev.dataFreshness ||
                    prev.databaseHealth ||
                    (Array.isArray(prev.refreshHistory) && prev.refreshHistory.length > 0) ||
                    prev.cronStatus
                );

                return {
                    ...prev,
                    loading: !hasExistingData,
                    refreshing: hasExistingData,
                    error: null
                };
            });
            
            const response = await fetch(API_ENDPOINTS.SYSTEM_STATUS);
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const data = await response.json();
            setSystemStatus(prev => ({
                ...prev,
                ...data,
                loading: false,
                refreshing: false
            }));
        } catch (error) {
            console.error('Failed to fetch system status:', error);
            setSystemStatus(prev => ({
                ...prev,
                error: error.message,
                loading: false,
                refreshing: false
            }));

            // Optional retry on initial load if requested
            if (withRetry && retryCount > 0) {
                setTimeout(() => {
                    fetchSystemStatus(false, retryCount - 1);
                }, RETRY_CONFIG.RETRY_DELAY_MS);
            }
        }
    }, []);

    const fetchRefreshProgress = useCallback(async () => {
        if (!refreshProgress.isRunning) return;
        
        try {
            const response = await fetch(API_ENDPOINTS.REFRESH_PROGRESS);
            if (response.ok) {
                const data = await response.json();
                setRefreshProgress(prev => ({
                    ...prev,
                    ...data,
                    source: prev.source === 'pipeline' ? prev.source : 'manual',
                    startTime: prev.startTime || data.startTime || prev.startTime
                }));
                
                // Stop polling if refresh completed
                if (data.isRunning === false) {
                    setRefreshProgress(prev => ({
                        ...prev,
                        isRunning: false,
                        source: prev.source === 'manual' ? null : prev.source
                    }));
                    fetchSystemStatus();
                }
            }
        } catch (error) {
            console.error('Failed to fetch refresh progress:', error);
        }
    }, [refreshProgress.isRunning, fetchSystemStatus]);

    const startRefresh = useCallback(async (options = {}) => {
        try {
            // Start the refresh via API
            const response = await fetch(API_ENDPOINTS.REFRESH_START, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(options)
            });

            if (!response.ok) {
                throw new Error(`Failed to start refresh: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Initialize progress state
            setRefreshProgress({
                isRunning: true,
                progress: 0,
                currentPhase: 'Starting refresh...',
                details: null,
                logs: [],
                refreshId: data.refreshId,
                startTime: new Date().toISOString(),
                source: 'manual'
            });

            // Start SSE streaming for real-time progress
            const eventSource = new EventSource(API_ENDPOINTS.REFRESH_PROGRESS_STREAM);
            setRefreshStream(eventSource);

            let streamStartTime = Date.now();
            let lastProgressTime = Date.now();

            eventSource.onmessage = (event) => {
                try {
                    const progressData = JSON.parse(event.data);
                    lastProgressTime = Date.now();
                    
                    switch (progressData.type) {
                        case 'connected':
                            break;
                        case 'status':
                            if (!progressData.isRunning) {
                                setRefreshProgress(prev => ({
                                    ...prev,
                                    isRunning: false,
                                    currentPhase: progressData.message || 'No active refresh',
                                    source: prev.source === 'manual' ? null : prev.source
                                }));
                            }
                            break;
                        case 'progress':
                            setRefreshProgress(prev => ({
                                ...prev,
                                isRunning: true,
                                progress: progressData.percentage || prev.progress,
                                currentPhase: progressData.stage || progressData.currentPhase || prev.currentPhase,
                                details: progressData.details || prev.details,
                                logs: prev.logs || [],
                                refreshId: progressData.refreshId || prev.refreshId,
                                startTime: prev.startTime || progressData.startTime || prev.startTime,
                                source: 'manual'
                            }));
                            break;
                        case 'completed':
                            setRefreshProgress(prev => ({
                                ...prev,
                                isRunning: false,
                                progress: 100,
                                currentPhase: 'Refresh completed',
                                details: progressData.details,
                                source: null
                            }));
                            eventSource.close();
                            setRefreshStream(null);
                            // Refresh system status after completion
                            fetchSystemStatus();
                            break;
                        case 'stopped':
                            console.log('🛑 Refresh stopped message received:', progressData);
                            setRefreshProgress(prev => ({
                                ...prev,
                                isRunning: false,
                                currentPhase: 'Refresh stopped',
                                details: progressData.message || 'Refresh was stopped by user',
                                source: null
                            }));
                            eventSource.close();
                            setRefreshStream(null);
                            // Refresh system status after stop
                            fetchSystemStatus();
                            break;
                        case 'stopping':
                            setRefreshProgress(prev => ({
                                ...prev,
                                currentPhase: 'Stopping...',
                                details: progressData.message || 'Stop signal received - finishing current batch...'
                            }));
                            break;
                        case 'keepalive':
                            break;
                        case 'error':
                            setRefreshProgress(prev => ({
                                ...prev,
                                isRunning: false,
                                currentPhase: 'Refresh failed',
                                details: progressData.error || progressData.message
                            }));
                            eventSource.close();
                            setRefreshStream(null);
                            break;
                        default:
                            break;
                    }
                } catch (error) {
                    console.error('Error parsing refresh progress:', error);
                }
            };

            eventSource.onerror = (error) => {
                console.error('Refresh progress stream error:', error);
                eventSource.close();
                setRefreshStream(null);
                setRefreshProgress(prev => ({
                    ...prev,
                    isRunning: false,
                    currentPhase: 'Connection Lost',
                    details: 'Stream connection lost - refresh may still be running on server',
                    source: prev.source === 'manual' ? null : prev.source
                }));
            };

            eventSource.onopen = function() {
                console.log('Refresh progress EventSource connected');
            };

            return { success: true, message: data.message };
        } catch (error) {
            setRefreshProgress(prev => ({
                ...prev,
                isRunning: false
            }));
            return { success: false, error: error.message };
        }
    }, [fetchSystemStatus]);

    const stopRefresh = useCallback(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.REFRESH_STOP, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to stop refresh: ${response.statusText}`);
            }

            const data = await response.json();
            
            // Close the SSE stream
            if (refreshStream) {
                refreshStream.close();
                setRefreshStream(null);
            }

            return { success: true, message: data.message };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [refreshStream]);

    const generateDatasets = useCallback(async (datasetTypes = []) => {
        try {
            const response = await fetch(API_ENDPOINTS.DATASETS_GENERATE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ datasetTypes })
            });

            if (!response.ok) {
                throw new Error(`Failed to generate datasets: ${response.statusText}`);
            }

            const data = await response.json();
            return { success: true, data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, []);

    // Pipeline actions
    const startPipeline = useCallback(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.PIPELINE_START, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to start pipeline: ${response.statusText}`);
            }

            const startContentType = response.headers.get('content-type') || '';
            const data = startContentType.includes('application/json') ? await response.json() : {};
            
            // Initialize progress state for pipeline
            setRefreshProgress({
                isRunning: true,
                progress: 0,
                currentPhase: 'Starting pipeline...',
                details: 'Initializing data refresh and dataset generation',
                logs: [],
                refreshId: null,
                startTime: new Date().toISOString(),
                source: 'pipeline'
            });
            setRefreshStream('pipeline');

            return { success: true, message: data?.message || 'Pipeline started successfully' };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, []);

    const getPipelineStatus = useCallback(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.PIPELINE_STATUS);

            if (!response.ok) {
                throw new Error(`Failed to fetch pipeline status: ${response.statusText}`);
            }

            const contentType = response.headers.get('content-type') || '';
            const data = contentType.includes('application/json') ? await response.json() : {};

            const active = data && typeof data === 'object' ? data.active : null;
            if (active) {
                setRefreshStream('pipeline');
                setRefreshProgress(prev => ({
                    ...prev,
                    isRunning: true,
                    progress: active.progress_percentage || 0,
                    currentPhase: active.phase || prev.currentPhase,
                    details: active.details || prev.details,
                    startTime: active.started_at || prev.startTime || new Date().toISOString(),
                    source: 'pipeline'
                }));
            } else {
                let wasPipeline = false;
                setRefreshProgress(prev => {
                    wasPipeline = prev.source === 'pipeline';
                    return {
                        ...prev,
                        isRunning: false,
                        currentPhase: prev.currentPhase || 'Idle',
                        source: wasPipeline ? null : prev.source
                    };
                });
                setRefreshStream(prev => (prev === 'pipeline' ? null : prev));
                if (wasPipeline && typeof fetchSystemStatus === 'function') {
                    // Refresh overall status once pipeline completes
                    fetchSystemStatus();
                }
            }

            return { success: true, data };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, [fetchSystemStatus]);

    const stopPipeline = useCallback(async () => {
        try {
            const response = await fetch(API_ENDPOINTS.PIPELINE_STOP, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `Failed to stop pipeline: ${response.statusText}`);
            }

            const data = await response.json();
            return { success: true, message: data.message || 'Pipeline stop signal sent' };
        } catch (error) {
            return { success: false, error: error.message };
        }
    }, []);

    // Poll for refresh progress only if no SSE stream is active
    useEffect(() => {
        // Only poll if refresh is running AND we don't have an active SSE stream
        if (refreshProgress.isRunning && refreshStream === null) {
            const interval = setInterval(fetchRefreshProgress, POLLING_CONFIG.REFRESH_PROGRESS_INTERVAL);
            return () => clearInterval(interval);
        }
    }, [refreshProgress.isRunning, refreshStream, fetchRefreshProgress]);

    // Disabled automatic system status fetch on mount to prevent server overload  
    // System status will be fetched when user explicitly accesses system section
    // useEffect(() => {
    //     fetchSystemStatus();
    // }, [fetchSystemStatus]);

    // Memoize the actions object to prevent infinite re-renders
    const actions = useMemo(() => ({
        refreshSystemStatus: fetchSystemStatus,
        startRefresh,
        stopRefresh,
        generateDatasets,
        startPipeline,
        getPipelineStatus,
        stopPipeline
    }), [fetchSystemStatus, startRefresh, stopRefresh, generateDatasets, startPipeline, getPipelineStatus, stopPipeline]);

    return {
        systemStatus,
        refreshProgress,
        actions
    };
};
